import { Link, createFileRoute } from "@tanstack/react-router";
import { Image } from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import {
  collectionsQueryOptions,
  useGetCollectionsQuery,
} from "@modules/shopify/hooks";
import { ALL_PRODUCTS_COLLECTION } from "@modules/shop/components/ProductsPage";
import { PageBreadcrumb } from "@modules/shared/Breadcrumbs/PageBreadcrumb";
import { ShoppingBag } from "lucide-react";
import {
  CardArrow,
  cardMedia,
  cardSurface,
} from "@modules/shared/Card/cardStyles";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/collections/",
)({
  component: CollectionsPage,
  ssr: true,
  loader: async ({ context: { queryClient }, params }) => {
    const localeVars = getCountryVariables(params.locale);
    await queryClient.ensureQueryData(
      collectionsQueryOptions({
        first: 50,
        ...localeVars,
      }),
    );
  },
  staleTime: 60_000,
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:collections.title"),
          description: t("Seo:collections.description"),
        }),
      ],
    };
  },
});

const EXCLUDED_COLLECTION_HANDLES = new Set([
  "novi-proizvodi-1",
  "najprodavanije",
  ALL_PRODUCTS_COLLECTION,
]);

function CollectionsPage() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();
  const localeVars = getCountryVariables(locale);

  const { data } = useGetCollectionsQuery({
    first: 50,
    ...localeVars,
  });

  const collections = (data?.collections.nodes ?? []).filter(
    (c) => !EXCLUDED_COLLECTION_HANDLES.has(c.handle),
  );

  return (
    <div className="mx-auto max-w-7xl px-4 pt-8 pb-24 sm:px-6 lg:px-8">
      <PageBreadcrumb
        segments={[
          {
            label: t("Collections.breadcrumbs.home"),
            to: "/$locale",
            params: { locale },
          },
          { label: t("Collections.title") },
        ]}
      />

      <header className="mt-10 max-w-2xl max-mobile:mt-8">
        <h1 className="text-3xlarge font-bold text-balance text-text-1 max-mobile:text-xlarge">
          {t("Collections.title")}
        </h1>
        <p className="mt-3 text-medium text-pretty text-text-3 max-mobile:text-base">
          {t("Collections.subtitle")}
        </p>
      </header>

      {collections.length > 0 ? (
        <ul className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-3 max-mobile:gap-3 max-mobile:mt-10">
          {collections.map((collection) => (
            <li key={collection.id}>
              <Link
                to="/$locale/collections/$handle"
                params={{ locale, handle: collection.handle }}
                className={`${cardSurface} h-full`}
              >
                <div className={`${cardMedia} aspect-4/3`}>
                  {collection.image?.url ? (
                    <Image
                      src={collection.image.url}
                      alt=""
                      className="size-full object-contain p-5 max-mobile:p-2 transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:transition-none"
                      loading="lazy"
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-background-6">
                      <ShoppingBag
                        className="size-8 text-text-4"
                        strokeWidth={1.5}
                        aria-hidden
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col px-2 pt-4 pb-2 max-mobile:px-1 max-mobile:pt-3 max-mobile:pb-1">
                  <h2 className="text-medium font-bold text-balance text-text-1 max-mobile:text-small">
                    {collection.title}
                  </h2>
                  {collection.description && (
                    <p className="mt-2 line-clamp-2 text-small text-pretty text-text-3 max-mobile:hidden">
                      {collection.description}
                    </p>
                  )}
                  <CardArrow className="mt-auto self-end max-mobile:hidden" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-12 rounded-2xl bg-background-7 py-20 text-center">
          <h2 className="mb-2 text-medium font-bold text-text-1">
            {t("Collections.emptyTitle")}
          </h2>
          <p className="text-small text-text-3">
            {t("Collections.emptySubtitle")}
          </p>
        </div>
      )}
    </div>
  );
}
